import { connect } from 'react-redux';
import { compose } from 'redux';
import iban from 'iban';
import { formValueSelector, change } from 'redux-form';
import PaymentModule from '../../components/compositions/form/PaymentModule';
import breakWithout from '../../components/basics/service/ServiceBreakWithout';
import {
  FORM_FIELD_PAYMENT_TYPE,
  FORM_FIELD_PAYMENT_TYPE_ADVANCE,
  FORM_FIELD_PAYMENT_TYPE_CC,
  FORM_FIELD_PAYMENT_TYPE_DEBIT,
  FORM_FIELD_PAYMENT_TYPE_PAYPAL,
} from '../../helpers/constants';

const defaultPaymentOptions = {
  directDebit: true,
  creditCard: true,
  advancePayment: true,
  paypal: true,
};

export const createFieldMap = (ui, params, paymentOptions = defaultPaymentOptions) => {
  const fieldMap = {
    mediaId: {
      name: 'media_id',
      label: ui.guiSymbolIban,
      hint: ui.guiPaymentGermanBanksOnly,
      normalize: value => iban.printFormat(value),
      validation: {
        isRequired: true,
        pattern: 'iban',
        dependsOn: { paymentType: FORM_FIELD_PAYMENT_TYPE_DEBIT },
      },
    },
    originOwner: {
      name: 'origin_owner',
      label: ui.guiPaymentAccountHolder,
      validation: {
        isRequired: false,
        dependsOn: { paymentType: FORM_FIELD_PAYMENT_TYPE_DEBIT },
        range: [0, 70],
      },
    },
    ddMandate: {
      name: 'dd_mandate',
      label: `${params.paymentDdLabel}`,
      hint: `${params.paymentDdCopy}`,
      type: 'checkbox',
      isControlled: false,
      validation: {
        isRequired: true,
        dependsOn: { paymentType: FORM_FIELD_PAYMENT_TYPE_DEBIT },
        valueEquals: true,
      },
    },
    creditCardToken: {
      name: 'credit_card_token',
      shouldSubmit: false,
      validation: {
        isRequired: true,
        dependsOn: { paymentType: FORM_FIELD_PAYMENT_TYPE_CC },
      },
    },
    creditCardHolder: {
      name: 'credit_card_holder',
      shouldSubmit: false,
      validation: {
        isRequired: true,
        dependsOn: { paymentType: FORM_FIELD_PAYMENT_TYPE_CC },
      },
    },
    creditCardData: {
      name: 'creditCardData',
      shouldSubmit: false,
    },
    provider: {
      name: 'provider',
      label: '',
      validation: {
        isRequired: true,
        dependsOn: { paymentType: FORM_FIELD_PAYMENT_TYPE_CC },
      },
    },
    shortenedCreditCardNumber: {
      name: 'shortened_credit_card_number',
      label: '',
      validation: {
        isRequired: true,
        dependsOn: { paymentType: FORM_FIELD_PAYMENT_TYPE_CC },
      },
    },
    expirationDate: {
      name: 'expiration_date',
      label: '',
      validation: {
        isRequired: true,
        dependsOn: { paymentType: FORM_FIELD_PAYMENT_TYPE_CC },
      },
    },
    paymentType: {
      name: FORM_FIELD_PAYMENT_TYPE,
      label: ui.myProfilePaymentType,
      validation: {
        isRequired: true,
      },
      options: [],
    },
  };

  if (paymentOptions.directDebit) {
    fieldMap.paymentType.options.push({
      label: ui.guiPaymentMethodDirectDebit,
      value: FORM_FIELD_PAYMENT_TYPE_DEBIT,
    });
  }
  if (paymentOptions.creditCard) {
    fieldMap.paymentType.options.push({
      label: ui.guiPaymentMethodCreditCard,
      value: FORM_FIELD_PAYMENT_TYPE_CC,
    });
  }
  if (paymentOptions.advancePayment) {
    fieldMap.paymentType.options.push({
      label: ui.guiPaymentMethodAdvance,
      value: FORM_FIELD_PAYMENT_TYPE_ADVANCE,
    });
  }
  if (paymentOptions.paypal) {
    fieldMap.paymentType.options.push({
      label: ui.paymentMethodPaypal,
      value: FORM_FIELD_PAYMENT_TYPE_PAYPAL,
    });
  }
  return fieldMap;
};

export const normalizeValues = (formConfig, values) => {
  const { fieldMap } = formConfig;

  const normValues = { ...values };

  if (normValues[fieldMap.paymentType.name] === FORM_FIELD_PAYMENT_TYPE_DEBIT) {
    normValues[fieldMap.mediaId.name] = iban.electronicFormat(normValues[fieldMap.mediaId.name]);
  }

  if (normValues[fieldMap.paymentType.name] === FORM_FIELD_PAYMENT_TYPE_CC) {
    // the middleware uses the media_id field for different payment types.
    // per default, we use the media_id and the origin_owner for
    // direct debit, so we need to replace it here
    // normValues[fieldMap.mediaId.name] = normValues[fieldMap.creditCardToken.name];
    normValues[fieldMap.originOwner.name] = normValues[fieldMap.creditCardHolder.name];
  } else {
    delete normValues[fieldMap.expirationDate.name];
    delete normValues[fieldMap.shortenedCreditCardNumber.name];
  }

  return normValues;
};

function mapStateToProps(state, props) {
  const {
    paymentHeadline, paymentCopy, fieldMap, form, formValues,
  } = props;
  const selector = formValueSelector(form);
  const paymentType = selector(state, fieldMap.paymentType.name);
  const creditCardData = selector(state, fieldMap.creditCardData.name);

  const personalData = state.user.customerData ? state.user.customerData : state.form[form].values;
  return {
    formValues,
    personalData,
    paymentHeadline,
    paymentCopy,
    paymentType,
    creditCardData,
  };
}

function mapDispatchToProps(dispatch, { fieldMap, form }) {
  return {
    onCreditCardDataAvailable: (response) => {
      const { brand, card, flexmodeToken, mediaId } = response.body.data;
      const { last4Digits, holder, expiryMonth, expiryYear } = card;
      dispatch(change(form, fieldMap.creditCardToken.name, flexmodeToken));
      dispatch(change(form, fieldMap.mediaId.name, mediaId));
      dispatch(change(form, fieldMap.provider.name, brand));
      dispatch(change(form, fieldMap.shortenedCreditCardNumber.name, last4Digits));
      const expirationDate = `${expiryYear}-${expiryMonth}`;
      dispatch(change(form, fieldMap.expirationDate.name, expirationDate));
      dispatch(change(form, fieldMap.creditCardHolder.name, holder));
      // store data object as a whole for easy access
      // TODO fix to meet old interface
      dispatch(change(form, fieldMap.creditCardData.name, { ...card, brand }));
    },
    resetCreditCardData: () => {
      dispatch(change(form, fieldMap.creditCardToken.name, ''));
      dispatch(change(form, fieldMap.mediaId.name, ''));
      dispatch(change(form, fieldMap.provider.name, ''));
      dispatch(change(form, fieldMap.shortenedCreditCardNumber.name, ''));
      dispatch(change(form, fieldMap.expirationDate.name, ''));
      dispatch(change(form, fieldMap.creditCardHolder.name, ''));
      // store data object as a whole for easy access
      // TODO fix to meet old interface
      dispatch(change(form, fieldMap.creditCardData.name, null));
    },
  };
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  breakWithout(['paymentType']),
)(PaymentModule);
